import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { isNoSignInPreviewActive } from '../debug'
import { firebaseAuth, firebaseFunctions, firestoreDb } from './firebase'
import { getWorkspacePulse } from './aiAgentBridge'
import type { PulseReport, PulseReportKind } from '../types/pulseReport'
import { PULSE_REPORT_KIND_COST, PULSE_REPORT_KIND_LABEL, PULSE_REPORT_RETENTION_MS } from '../types/pulseReport'

type Listener = () => void

const offlineQueueKey = (workspaceId: string) => `timelining:pulse-report-queue:${workspaceId}`
const debugReports = new Map<string, PulseReport[]>()
const queuedReports = new Map<string, PulseReport[]>()
const remoteReports = new Map<string, PulseReport[]>()
const listeners = new Map<string, Set<Listener>>()

const now = () => Date.now()
const timestampMs = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value instanceof Date) return value.getTime()
  if (value && typeof value === 'object' && 'toMillis' in value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    const millis = (value as { toMillis: () => number }).toMillis()
    return Number.isFinite(millis) ? millis : 0
  }
  return 0
}

const asReport = (snapshot: QueryDocumentSnapshot<DocumentData>): PulseReport => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    ...data,
    createdAtMs: Number(data.createdAtMs ?? timestampMs(data.createdAt)),
    updatedAtMs: Number(data.updatedAtMs ?? timestampMs(data.updatedAt)),
    expiresAtMs: data.expiresAtMs ?? (timestampMs(data.expiresAt) || null),
  } as PulseReport
}

const emit = (workspaceId: string) => {
  listeners.get(workspaceId)?.forEach((listener) => listener())
}

const readQueue = (workspaceId: string): PulseReport[] => {
  if (queuedReports.has(workspaceId)) return queuedReports.get(workspaceId) ?? []
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(offlineQueueKey(workspaceId))
    const parsed = raw ? JSON.parse(raw) : []
    const reports = Array.isArray(parsed) ? parsed as PulseReport[] : []
    queuedReports.set(workspaceId, reports)
    return reports
  } catch {
    queuedReports.set(workspaceId, [])
    return []
  }
}

const writeQueue = (workspaceId: string, reports: PulseReport[]) => {
  queuedReports.set(workspaceId, reports)
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(offlineQueueKey(workspaceId), JSON.stringify(reports))
  }
  emit(workspaceId)
}

const localReports = (workspaceId: string) => [
  ...(debugReports.get(workspaceId) ?? []),
  ...readQueue(workspaceId),
]

const mergeReports = (remote: PulseReport[], workspaceId: string) => {
  const byId = new Map<string, PulseReport>()
  for (const report of [...localReports(workspaceId), ...remote]) {
    if (report.status !== 'ARCHIVED') byId.set(report.id, report)
  }
  return [...byId.values()].sort((a, b) => b.createdAtMs - a.createdAtMs)
}

export const makeDebugPulseReport = async (workspaceId: string, kind: PulseReportKind): Promise<PulseReport> => {
  const createdAtMs = now()
  const pulse = await getWorkspacePulse(workspaceId)
  const label = PULSE_REPORT_KIND_LABEL[kind]
  return {
    id: `debug-pulse-report-${kind.toLowerCase()}-${createdAtMs}`,
    workspaceId,
    createdBy: firebaseAuth.currentUser?.uid ?? 'debug-user',
    kind,
    status: 'READY',
    createdAtMs,
    creditsCharged: PULSE_REPORT_KIND_COST[kind],
    expiresAtMs: createdAtMs + PULSE_REPORT_RETENTION_MS,
    inputs: {
      windowMs: 24 * 60 * 60 * 1000,
      pulseFingerprint: `debug-${workspaceId}-${kind}-${pulse.riskScore}`,
      riskScoreAtRun: pulse.riskScore,
      healthStateAtRun: pulse.healthState,
    },
    content: {
      headline: `${label} report: ${pulse.healthState.toLowerCase()} at ${pulse.riskScore}/100`,
      narrativeMarkdown: `# ${label} report\n\nWorkspace health is ${pulse.healthState.toLowerCase()} with risk score ${pulse.riskScore}/100.\n\nOpen risks: ${pulse.openRiskCount}. Failed automations: ${pulse.failedAutomationCount}. Stock pressure: ${pulse.stockShortageCount}.`,
      metrics: pulse,
      sections: [
        { id: 'summary', title: 'Summary', kind: 'NARRATIVE', body: `Risk score ${pulse.riskScore}/100 with ${pulse.activity.last24hCount} recent activity events.` },
      ],
    },
  }
}

export const subscribePulseReports = (
  workspaceId: string,
  _uid: string,
  onChange: (reports: PulseReport[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe => {
  const listener = () => onChange(mergeReports(remoteReports.get(workspaceId) ?? [], workspaceId))
  if (!listeners.has(workspaceId)) listeners.set(workspaceId, new Set())
  listeners.get(workspaceId)?.add(listener)

  const unsubscribe = onSnapshot(
    query(collection(firestoreDb, 'workspaces', workspaceId, 'pulse_reports'), orderBy('createdAtMs', 'desc')),
    (snapshot) => {
      const reports = snapshot.docs.map(asReport)
      remoteReports.set(workspaceId, reports)
      onChange(mergeReports(reports, workspaceId))
    },
    (error) => {
      onError?.(error)
      onChange(mergeReports([], workspaceId))
    },
  )

  return () => {
    listeners.get(workspaceId)?.delete(listener)
    unsubscribe()
  }
}

export const runPulseReport = async (input: {
  workspaceId: string
  kind: PulseReportKind
  inputs?: { windowMs?: number }
}): Promise<{ reportId: string; cached?: boolean; creditsCharged?: number }> => {
  if (isNoSignInPreviewActive()) {
    const report = await makeDebugPulseReport(input.workspaceId, input.kind)
    debugReports.set(input.workspaceId, [report, ...(debugReports.get(input.workspaceId) ?? [])])
    emit(input.workspaceId)
    return { reportId: report.id, cached: false, creditsCharged: report.creditsCharged }
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const createdAtMs = now()
    const queued: PulseReport = {
      id: `queued-pulse-report-${input.kind.toLowerCase()}-${createdAtMs}`,
      workspaceId: input.workspaceId,
      createdBy: firebaseAuth.currentUser?.uid ?? '',
      kind: input.kind,
      status: 'PENDING',
      createdAtMs,
      creditsCharged: 0,
      inputs: { windowMs: input.inputs?.windowMs },
      content: {
        headline: `${PULSE_REPORT_KIND_LABEL[input.kind]} report queued`,
        narrativeMarkdown: '',
        metrics: await getWorkspacePulse(input.workspaceId),
        sections: [],
      },
    }
    writeQueue(input.workspaceId, [queued, ...readQueue(input.workspaceId)])
    return { reportId: queued.id, cached: false, creditsCharged: 0 }
  }
  await firebaseAuth.currentUser?.getIdToken(true)
  const call = httpsCallable<typeof input, { reportId: string; cached?: boolean; creditsCharged?: number }>(firebaseFunctions, 'generatePulseReport')
  return (await call(input)).data
}

export const drainQueuedPulseReports = async (workspaceId: string) => {
  const queued = readQueue(workspaceId)
  if (!queued.length || (typeof navigator !== 'undefined' && navigator.onLine === false)) return
  const remaining: PulseReport[] = []
  for (const report of queued) {
    try {
      await runPulseReport({ workspaceId, kind: report.kind, inputs: report.inputs })
    } catch {
      remaining.push({ ...report, status: 'FAILED', error: 'Retry failed. Open this tab while online to try again.' })
    }
  }
  writeQueue(workspaceId, remaining)
}

export const pinPulseReport = async (workspaceId: string, reportId: string, pinned: boolean) => {
  if (reportId.startsWith('debug-') || reportId.startsWith('queued-')) return
  const call = httpsCallable<{ workspaceId: string; reportId: string; pinned: boolean }, { reportId: string; pinned: boolean }>(firebaseFunctions, 'pinPulseReport')
  await call({ workspaceId, reportId, pinned })
}

export const deletePulseReport = async (workspaceId: string, reportId: string) => {
  if (reportId.startsWith('debug-')) {
    debugReports.set(workspaceId, (debugReports.get(workspaceId) ?? []).filter((report) => report.id !== reportId))
    emit(workspaceId)
    return
  }
  if (reportId.startsWith('queued-')) {
    writeQueue(workspaceId, readQueue(workspaceId).filter((report) => report.id !== reportId))
    return
  }
  const call = httpsCallable<{ workspaceId: string; reportId: string }, { reportId: string; status: 'ARCHIVED' }>(firebaseFunctions, 'deletePulseReport')
  await call({ workspaceId, reportId })
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    for (const workspaceId of queuedReports.keys()) void drainQueuedPulseReports(workspaceId)
  })
}
